import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import {
    Database,
    Plus,
    ChevronDown,
    ChevronRight,
    AlertCircle,
    AlertTriangle,
    Eye,
    Lock,
    Globe,
    User,
    Users,
    Settings,
    Info,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@shadcn-in-nx/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import { useChatContext } from "../ChatContext";
import { useAuth } from "../../context/AuthContext";
import ObfuscationConfigPanel from "./ObfuscationConfigPanel";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Label } from "@shadcn-in-nx/ui/label";
import { Input } from "@shadcn-in-nx/ui/input";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@shadcn-in-nx/ui/card";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@shadcn-in-nx/ui/tabs";

function DataSecurityPanel({
    workflowId,
    componentsResponse,
    requiresLogin = true,
}) {
    const { t } = useTranslation();
    const { selectedEnvironment } = useChatContext();
    const { isFeatureEnabled, canModifyObfuscation } = useAuth();
    const isEnterprise = isFeatureEnabled("dataObfuscation");
    const [expandedTables, setExpandedTables] = useState({});
    const [obfuscationConfigs, setObfuscationConfigs] = useState({}); // { entityId: { fields: {}, rawAccessRules: [] } }
    const [tableAccess, setTableAccess] = useState({});
    const [editingRules, setEditingRules] = useState({}); // { entityId: { read: [], create: [], update: [], delete: [] } }
    const [ruleOperators, setRuleOperators] = useState({}); // { entityId: { read: "or", create: "or", ... } }
    const [activeTab, setActiveTab] = useState({}); // { entityId: "read" }
    const [selectedTemplate, setSelectedTemplate] = useState({}); // { entityId: templateId }
    const [showAdvanced, setShowAdvanced] = useState({}); // { entityId: boolean }
    const [entities, setEntities] = useState([]); // Entity components

    // Fetch table access for the workflow
    const { call: fetchTableAccess } = useBluelagoon({
        method: "getWorkflowTableAccess",
    });

    const { call: saveTableAccess, isLoading: isSavingAccess } = useBluelagoon({
        method: "createWorkflowTableAccess",
    });

    const [userFields, setUserFields] = useState([]);
    const [entityFields, setEntityFields] = useState({}); // { entityId: [fields] }

    // Load entity components from the passed componentsResponse prop
    const { call: fetchComponentCode } = useBluelagoon({ method: "getComponentCode" });

    const loadEntities = useCallback(async () => {
        try {
            if (!componentsResponse) {
                return;
            }

            // Filter for ENTITY type components, excluding User entity
            const entityComponents = componentsResponse.filter(
                (component) =>
                    component.type === "ENTITY" &&
                    component.status === "ACTIVE" &&
                    component.name !== "User" &&
                    component.name !== "User.json"
            );

            // Fetch code for entity components that don't have it
            const codeFetches = entityComponents
                .filter((c) => !c.code)
                .map((c) =>
                    fetchComponentCode({ id: c.id })
                        .then((res) => ({ id: c.id, code: res?.data?.code || "" }))
                );
            const fetched = await Promise.all(codeFetches);
            const codeMap = Object.fromEntries(fetched.map((f) => [f.id, f.code]));

            // Extract schemas from entity components
            const entitiesWithSchemas = entityComponents.map((component) => {
                let schema = {};
                const code = component.code || codeMap[component.id];

                // Try to parse code field as JSON to get schema
                if (code) {
                    try {
                        const codeData = JSON.parse(code);
                        if (codeData.schema) {
                            schema = codeData.schema;
                        } else if (codeData.properties) {
                            schema = codeData.properties;
                        } else if (codeData.fields) {
                            schema = codeData.fields;
                        } else if (
                            typeof codeData === "object" &&
                            !Array.isArray(codeData)
                        ) {
                            schema = codeData;
                        }
                    } catch (e) {
                        console.warn(
                            "Failed to parse component code:",
                            component.name,
                            e
                        );
                    }
                }

                // Fallback to metadata if code parsing didn't work
                if (Object.keys(schema).length === 0 && component.metadata) {
                    if (component.metadata.schema) {
                        schema = component.metadata.schema;
                    } else if (component.metadata.properties) {
                        schema = component.metadata.properties;
                    } else if (component.metadata.fields) {
                        schema = component.metadata.fields;
                    }
                }

                return {
                    ...component,
                    schema,
                    fields: [
                        "id",
                        ...Object.keys(schema).filter((f) => f !== "id"),
                    ],
                };
            });

            setEntities(entitiesWithSchemas);

            // Pre-populate entity fields
            const fieldsMap = {};
            entitiesWithSchemas.forEach((entity) => {
                fieldsMap[entity.id] = entity.fields;
            });
            setEntityFields(fieldsMap);

            // Set User fields - find User entity from all components (not filtered)
            const userEntity = componentsResponse?.find(
                (component) =>
                    component.type === "ENTITY" &&
                    component.status === "ACTIVE" &&
                    (component.name === "User" ||
                        component.name === "User.json")
            );

            if (userEntity) {
                // Parse custom fields from the User entity schema
                let userSchema = {};
                let userCode = userEntity.code || codeMap[userEntity.id];
                if (!userCode) {
                    const res = await fetchComponentCode({ id: userEntity.id });
                    userCode = res?.data?.code || "";
                }
                if (userCode) {
                    try {
                        const codeData = JSON.parse(userCode);
                        if (codeData.schema) {
                            userSchema = codeData.schema;
                        } else if (codeData.properties) {
                            userSchema = codeData.properties;
                        } else if (codeData.fields) {
                            userSchema = codeData.fields;
                        } else if (
                            typeof codeData === "object" &&
                            !Array.isArray(codeData)
                        ) {
                            userSchema = codeData;
                        }
                    } catch (e) {
                        console.warn("Failed to parse User entity code:", e);
                    }
                }

                // Fallback to metadata if code parsing didn't work
                if (
                    Object.keys(userSchema).length === 0 &&
                    userEntity.metadata
                ) {
                    if (userEntity.metadata.schema) {
                        userSchema = userEntity.metadata.schema;
                    } else if (userEntity.metadata.properties) {
                        userSchema = userEntity.metadata.properties;
                    } else if (userEntity.metadata.fields) {
                        userSchema = userEntity.metadata.fields;
                    }
                }

                const customUserFields = Object.keys(userSchema);
                const defaultFields = ["email", "userId", "role"];
                const allFields = [
                    ...defaultFields,
                    ...customUserFields.filter(
                        (f) => !defaultFields.includes(f)
                    ),
                ];
                setUserFields(allFields);
            } else {
                setUserFields(["email", "userId", "role"]);
            }
        } catch (error) {
            console.error("Error loading entities:", error);
        }
    }, [componentsResponse, fetchComponentCode]);

    const loadTableAccess = useCallback(async () => {
        try {
            const response = await fetchTableAccess({ workflowId });

            if (response?.data) {
                const accessMap = {};
                const accessList = Array.isArray(response.data)
                    ? response.data
                    : response.data.data || [];

                accessList.forEach((access) => {
                    if (access.tableId) {
                        // Map by tableId (database table ID)
                        accessMap[access.tableId] = access;

                        // Also map by table name for entity matching
                        if (access.table?.name) {
                            const tableName = access.table.name.toLowerCase();
                            accessMap[tableName] = access;
                        }
                    }
                });

                setTableAccess(accessMap);
            }
        } catch (error) {
            console.error("Error loading table access:", error);
        }
    }, [fetchTableAccess, workflowId]);

    useEffect(() => {
        if (workflowId) {
            loadTableAccess();
        }
    }, [workflowId, loadTableAccess]);

    // Load entities when componentsResponse changes
    useEffect(() => {
        if (componentsResponse) {
            loadEntities();
        }
    }, [componentsResponse, loadEntities]);

    const toggleTable = (tableId) => {
        setExpandedTables((prev) => ({
            ...prev,
            [tableId]: !prev[tableId],
        }));
    };

    // Template definitions - Human-friendly language
    const templates = [
        {
            id: "only-mine",
            icon: User,
            title: t("dataSecurity.templates.onlyMine.title") || "Only me",
            description:
                t("dataSecurity.templates.onlyMine.description") ||
                "Only the person who created the record can access it",
            rules: {
                read: [
                    {
                        id: "creator-1",
                        type: "creator",
                        config: { matchType: "value" },
                    },
                ],
                create: [
                    {
                        id: "creator-2",
                        type: "creator",
                        config: { matchType: "value" },
                    },
                ],
                update: [
                    {
                        id: "creator-3",
                        type: "creator",
                        config: { matchType: "value" },
                    },
                ],
                delete: [
                    {
                        id: "creator-4",
                        type: "creator",
                        config: { matchType: "value" },
                    },
                ],
            },
        },
        {
            id: "admin-only",
            icon: Users,
            title: t("dataSecurity.templates.adminOnly.title") || "Admins only",
            description:
                t("dataSecurity.templates.adminOnly.description") ||
                "Restricted to workspace administrators",
            rules: {
                read: [
                    {
                        id: "admin-1",
                        type: "userField",
                        config: {
                            matchType: "value",
                            userField: "role",
                            matchValue: "admin",
                        },
                    },
                ],
                create: [
                    {
                        id: "admin-2",
                        type: "userField",
                        config: {
                            matchType: "value",
                            userField: "role",
                            matchValue: "admin",
                        },
                    },
                ],
                update: [
                    {
                        id: "admin-3",
                        type: "userField",
                        config: {
                            matchType: "value",
                            userField: "role",
                            matchValue: "admin",
                        },
                    },
                ],
                delete: [
                    {
                        id: "admin-4",
                        type: "userField",
                        config: {
                            matchType: "value",
                            userField: "role",
                            matchValue: "admin",
                        },
                    },
                ],
            },
        },
        {
            id: "everyone-logged-in",
            icon: Lock,
            title:
                t("dataSecurity.templates.everyoneLoggedIn.title") ||
                "Signed-in users",
            description:
                t("dataSecurity.templates.everyoneLoggedIn.description") ||
                "Anyone logged in can access these records",
            rules: {
                read: [],
                create: [],
                update: [],
                delete: [],
            },
            requiresLoginOnly: true,
        },
        {
            id: "authenticated",
            icon: Lock,
            title:
                t("dataSecurity.templates.authenticated.title") ||
                "Signed-in users",
            description:
                t("dataSecurity.templates.authenticated.description") ||
                "Anyone logged in can access these records",
            rules: {
                read: [
                    {
                        id: "auth-1",
                        type: "authenticated",
                        config: {},
                    },
                ],
                create: [
                    {
                        id: "auth-2",
                        type: "authenticated",
                        config: {},
                    },
                ],
                update: [
                    {
                        id: "auth-3",
                        type: "authenticated",
                        config: {},
                    },
                ],
                delete: [
                    {
                        id: "auth-4",
                        type: "authenticated",
                        config: {},
                    },
                ],
            },
            requiresLoginOnly: false,
        },
        {
            id: "public",
            icon: Globe,
            title: t("dataSecurity.templates.public.title") || "Public",
            description:
                t("dataSecurity.templates.public.description") ||
                "Anyone can access, no login required",
            rules: {
                read: [],
                create: [],
                update: [],
                delete: [],
            },
            requiresLoginOnly: false,
        },
        {
            id: "custom",
            icon: Settings,
            title: t("dataSecurity.templates.custom.title") || "Custom rules",
            description:
                t("dataSecurity.templates.custom.description") ||
                "Create your own custom access rules",
            rules: null, // Keep existing rules
        },
    ];

    // Apply template to table
    const applyTemplate = (tableId, templateId) => {
        const template = templates.find((t) => t.id === templateId);
        if (!template) return;

        setSelectedTemplate((prev) => ({ ...prev, [tableId]: templateId }));

        if (template.id === "custom") {
            // Show advanced interface
            setShowAdvanced((prev) => ({ ...prev, [tableId]: true }));
        } else {
            // Apply template rules
            if (template.rules) {
                setEditingRules((prev) => ({
                    ...prev,
                    [tableId]: template.rules,
                }));
            }
            // Hide advanced interface by default
            setShowAdvanced((prev) => ({ ...prev, [tableId]: false }));
        }
    };

    // Convert MongoDB query format to friendly rules (per operation)
    // Returns { rules: {...}, operators: {...} }
    const parseMongoQueryToRules = (securityFilter) => {
        if (!securityFilter) {
            return {
                rules: {
                    read: [],
                    create: [],
                    update: [],
                    delete: [],
                },
                operators: {
                    read: "or",
                    create: "or",
                    update: "or",
                    delete: "or",
                },
            };
        }

        const rulesByOperation = {
            read: [],
            create: [],
            update: [],
            delete: [],
        };

        const operatorsByOperation = {
            read: "or",
            create: "or",
            update: "or",
            delete: "or",
        };

        const operations = ["read", "create", "update", "delete"];

        operations.forEach((operation) => {
            if (!securityFilter[operation]) return;

            const query = securityFilter[operation];

            // Helper to add rule(s) - handles both single rules and arrays
            const addRules = (ruleData) => {
                if (!ruleData) return;
                if (Array.isArray(ruleData)) {
                    rulesByOperation[operation].push(...ruleData);
                } else {
                    rulesByOperation[operation].push(ruleData);
                }
            };

            // Handle $or queries (multiple conditions)
            if (query.$or && Array.isArray(query.$or)) {
                operatorsByOperation[operation] = "or";
                query.$or.forEach((condition) => {
                    addRules(parseConditionToRule(condition));
                });
            } else if (query.$and && Array.isArray(query.$and)) {
                // Handle $and queries (all conditions must match)
                operatorsByOperation[operation] = "and";
                query.$and.forEach((condition) => {
                    addRules(parseConditionToRule(condition));
                });
            } else {
                // Single condition
                addRules(parseConditionToRule(query));
            }
        });

        return { rules: rulesByOperation, operators: operatorsByOperation };
    };

    const parseConditionToRule = (condition) => {
        // Check for authenticated rule (requires login)
        if (condition.authenticated === true) {
            return {
                id: "authenticated",
                type: "authenticated",
                config: {},
            };
        }

        // Check for creator rule (createdBy: {{user.email}})
        if (condition.createdBy === "{{user.email}}") {
            return {
                id: "creator",
                type: "creator",
                config: {},
            };
        }

        // Check for user_condition (user field value matching)
        // Format: { user_condition: { fieldName: "expectedValue" } }
        // This checks if user has a specific field value (role, email, custom fields, etc.)
        // Note: Multi-field conditions are returned as an array
        if (
            condition.user_condition &&
            typeof condition.user_condition === "object"
        ) {
            const fields = Object.entries(condition.user_condition);
            if (fields.length > 0) {
                // Return array of rules for multi-field conditions
                const rules = fields.map(([userField, matchValue]) => ({
                    id: `userField-${userField}-${matchValue}`,
                    type: "userField",
                    config: {
                        userField,
                        matchType: "value",
                        matchValue,
                    },
                }));
                // Return single rule or array depending on count
                return rules.length === 1 ? rules[0] : rules;
            }
        }

        // Check for user field match (e.g., departmentId: {{user.departmentId}})
        const userFieldKey = Object.keys(condition).find(
            (key) =>
                key !== "user_condition" &&
                key !== "createdBy" &&
                typeof condition[key] === "string" &&
                condition[key].startsWith("{{user.")
        );
        if (userFieldKey) {
            const userField = condition[userFieldKey]
                .replace("{{user.", "")
                .replace("}}", "");
            return {
                id: `userField-${userField}-${userFieldKey}`,
                type: "userField",
                config: {
                    userField,
                    matchType: "field", // Match against entity field
                    entityField: userFieldKey,
                },
            };
        }

        // Check for direct field value match (e.g., status: "open")
        // This is an entity field filter (not user-based)
        const directFieldMatch = Object.keys(condition).find(
            (key) =>
                key !== "user_condition" &&
                key !== "createdBy" &&
                typeof condition[key] === "string" &&
                !condition[key].startsWith("{{")
        );
        if (directFieldMatch) {
            return {
                id: `entityField-${directFieldMatch}-${condition[directFieldMatch]}`,
                type: "entityField",
                config: {
                    fieldName: directFieldMatch,
                    fieldValue: condition[directFieldMatch],
                },
            };
        }

        return null;
    };

    // Normalize name by stripping separators for flexible matching
    const normalizeName = (name) => name.toLowerCase().replace(/[-_\s]/g, "");

    // Helper function to find access record for an entity
    const findAccessForEntity = (entity) => {
        // Try direct match by entity ID first
        if (tableAccess[entity.id]) {
            return tableAccess[entity.id];
        }

        // Extract entity name and normalize
        let entityName = entity.name.replace(/\.json$/i, "").toLowerCase();

        // Helper function to pluralize (matching backend logic)
        const pluralize = (name) => {
            if (!name) return name;
            if (name.endsWith("y") && !/[aeiou]y$/.test(name)) {
                return name.slice(0, -1) + "ies";
            } else if (
                name.endsWith("s") ||
                name.endsWith("x") ||
                name.endsWith("z") ||
                name.endsWith("ch") ||
                name.endsWith("sh")
            ) {
                return name + "es";
            } else if (name.endsWith("f")) {
                return name.slice(0, -1) + "ves";
            } else if (name.endsWith("fe")) {
                return name.slice(0, -2) + "ves";
            } else {
                return name + "s";
            }
        };

        // Try to find by entity name (singular)
        if (tableAccess[entityName]) {
            return tableAccess[entityName];
        }

        // Try plural form
        const pluralName = pluralize(entityName);
        if (tableAccess[pluralName]) {
            return tableAccess[pluralName];
        }

        // Try with 's' suffix
        if (tableAccess[entityName + "s"]) {
            return tableAccess[entityName + "s"];
        }

        // Try normalized matching (strips hyphens, underscores, spaces)
        const normalizedEntity = normalizeName(entityName);
        const normalizedPlural = normalizeName(pluralName);
        for (const [key, value] of Object.entries(tableAccess)) {
            const normalizedKey = normalizeName(key);
            if (
                normalizedKey === normalizedEntity ||
                normalizedKey === normalizedPlural ||
                normalizedKey === normalizedEntity + "s"
            ) {
                return value;
            }
        }

        return null;
    };

    // Pre-compute parsed rules for all entities to avoid creating new objects during render
    const parsedRulesByEntity = useMemo(() => {
        const map = {};
        entities.forEach((entity) => {
            const access = findAccessForEntity(entity);
            map[entity.id] = parseMongoQueryToRules(access?.securityFilter);
        });
        return map;
    }, [entities, tableAccess]);

    const startEditing = (entity) => {
        // Entity fields are already loaded from loadEntities()
        // Find the access record for this entity
        const entityAccess = findAccessForEntity(entity);
        const parsed = entityAccess?.securityFilter
            ? parseMongoQueryToRules(entityAccess.securityFilter)
            : {
                  rules: { read: [], create: [], update: [], delete: [] },
                  operators: {
                      read: "or",
                      create: "or",
                      update: "or",
                      delete: "or",
                  },
              };
        const initialRules = parsed.rules;
        const initialOperators = parsed.operators;
        setEditingRules((prev) => ({
            ...prev,
            [entity.id]: initialRules,
        }));
        setRuleOperators((prev) => ({
            ...prev,
            [entity.id]: initialOperators,
        }));
        setActiveTab((prev) => ({
            ...prev,
            [entity.id]: "read",
        }));

        // Detect which template matches current rules
        const detectedTemplate = detectTemplate(initialRules);
        setSelectedTemplate((prev) => ({
            ...prev,
            [entity.id]: detectedTemplate,
        }));
        setShowAdvanced((prev) => ({
            ...prev,
            [entity.id]: detectedTemplate === "custom",
        }));

        // Load obfuscation config from existing securityFilter
        const obfuscation = entityAccess?.securityFilter?.obfuscation || {
            fields: {},
            rawAccessRules: [],
        };
        setObfuscationConfigs((prev) => ({
            ...prev,
            [entity.id]: obfuscation,
        }));
    };

    // Detect which template the current rules match
    const detectTemplate = (rules) => {
        // Check if all operations are empty
        const allEmpty = Object.values(rules).every(
            (operationRules) => operationRules.length === 0
        );
        if (allEmpty) {
            // Return different template based on requiresLogin setting
            return requiresLogin ? "everyone-logged-in" : "public";
        }

        // Check if all operations have only authenticated rules
        const allAuthenticated = Object.values(rules).every(
            (operationRules) =>
                operationRules.length > 0 &&
                operationRules.every((rule) => rule.type === "authenticated")
        );
        if (allAuthenticated) return "authenticated";

        // Check if all operations have only creator rules
        const allCreator = Object.values(rules).every(
            (operationRules) =>
                operationRules.length > 0 &&
                operationRules.every((rule) => rule.type === "creator")
        );
        if (allCreator) return "only-mine";

        // Check if all operations require admin role
        const allAdmin = Object.values(rules).every(
            (operationRules) =>
                operationRules.length > 0 &&
                operationRules.every(
                    (rule) =>
                        rule.type === "userField" &&
                        rule.config?.userField === "role" &&
                        rule.config?.matchValue === "admin"
                )
        );
        if (allAdmin) return "admin-only";

        // Otherwise it's custom
        return "custom";
    };

    const cancelEditing = (tableId) => {
        setEditingRules((prev) => {
            const newRules = { ...prev };
            delete newRules[tableId];
            return newRules;
        });
        setActiveTab((prev) => {
            const newTabs = { ...prev };
            delete newTabs[tableId];
            return newTabs;
        });
    };

    const addRule = (tableId, operation) => {
        setEditingRules((prev) => ({
            ...prev,
            [tableId]: {
                ...prev[tableId],
                [operation]: [
                    ...(prev[tableId]?.[operation] || []),
                    {
                        id: Date.now().toString(),
                        type: "creator",
                        config: {
                            matchType: "value",
                        },
                    },
                ],
            },
        }));
    };

    const removeRule = (tableId, operation, ruleId) => {
        setEditingRules((prev) => ({
            ...prev,
            [tableId]: {
                ...prev[tableId],
                [operation]: prev[tableId][operation].filter(
                    (rule) => rule.id !== ruleId
                ),
            },
        }));
    };

    const updateRule = (tableId, operation, ruleId, updates) => {
        setEditingRules((prev) => ({
            ...prev,
            [tableId]: {
                ...prev[tableId],
                [operation]: prev[tableId][operation].map((rule) =>
                    rule.id === ruleId ? { ...rule, ...updates } : rule
                ),
            },
        }));
    };

    // Toggle operator between "and" and "or" for a specific operation
    const toggleOperator = (tableId, operation) => {
        setRuleOperators((prev) => {
            const currentOperators = prev[tableId] || {
                read: "or",
                create: "or",
                update: "or",
                delete: "or",
            };
            return {
                ...prev,
                [tableId]: {
                    ...currentOperators,
                    [operation]:
                        currentOperators[operation] === "or" ? "and" : "or",
                },
            };
        });
    };

    // Convert friendly rules to MongoDB query format
    const convertRulesToMongoQuery = (
        rulesByOperation,
        operatorsByOperation = {}
    ) => {
        const mongoQuery = {};
        const operations = ["read", "create", "update", "delete"];

        operations.forEach((operation) => {
            const rulesForOperation = rulesByOperation[operation] || [];
            const operator = operatorsByOperation[operation] || "or";

            if (rulesForOperation.length === 0) return;

            // Convert each rule to a MongoDB condition
            const conditions = rulesForOperation
                .map((rule) => {
                    if (rule.type === "authenticated") {
                        // Authenticated rule - just requires user to be logged in
                        return { authenticated: true };
                    } else if (rule.type === "creator") {
                        return { createdBy: "{{user.email}}" };
                    } else if (rule.type === "userField") {
                        if (!rule.config.userField) {
                            return null;
                        }

                        // Handle value-based matching (e.g., role = "admin", email = "user@example.com")
                        if (rule.config.matchType === "value") {
                            if (!rule.config.matchValue) {
                                return null;
                            }
                            // Use user_condition format for all user field value matching
                            return {
                                user_condition: {
                                    [rule.config.userField]:
                                        rule.config.matchValue,
                                },
                            };
                        }

                        // Handle field-based matching (e.g., departmentId = {{user.departmentId}})
                        if (rule.config.matchType === "field") {
                            if (!rule.config.entityField) {
                                return null;
                            }
                            return {
                                [rule.config
                                    .entityField]: `{{user.${rule.config.userField}}}`,
                            };
                        }
                    } else if (rule.type === "entityField") {
                        // Handle entity field value matching (e.g., status = "open")
                        if (!rule.config.fieldName || !rule.config.fieldValue) {
                            return null;
                        }
                        return {
                            [rule.config.fieldName]: rule.config.fieldValue,
                        };
                    }
                    return null;
                })
                .filter((c) => c !== null);

            // If only one condition, save it directly
            if (conditions.length === 1) {
                mongoQuery[operation] = conditions[0];
            } else if (conditions.length > 1) {
                // Multiple conditions, use $or or $and based on operator
                mongoQuery[operation] =
                    operator === "and"
                        ? { $and: conditions }
                        : { $or: conditions };
            }
        });

        return mongoQuery;
    };

    const saveRules = async (table) => {
        try {
            const rulesByOperation = editingRules[table.id] || {
                read: [],
                create: [],
                update: [],
                delete: [],
            };
            const operatorsByOperation = ruleOperators[table.id] || {
                read: "or",
                create: "or",
                update: "or",
                delete: "or",
            };

            // Convert friendly rules to MongoDB query format
            const mongoQuery = convertRulesToMongoQuery(
                rulesByOperation,
                operatorsByOperation
            );

            // Merge obfuscation config if any fields are configured
            const obfuscation = obfuscationConfigs[table.id];
            if (
                obfuscation &&
                Object.keys(obfuscation.fields || {}).length > 0
            ) {
                mongoQuery.obfuscation = obfuscation;
            }

            // Determine overall access type based on rules
            let accessType = "read";
            const hasWrite =
                (rulesByOperation.create &&
                    rulesByOperation.create.length > 0) ||
                (rulesByOperation.update &&
                    rulesByOperation.update.length > 0) ||
                (rulesByOperation.delete && rulesByOperation.delete.length > 0);
            const hasRead =
                rulesByOperation.read && rulesByOperation.read.length > 0;

            if (hasWrite && hasRead) {
                accessType = "readwrite";
            } else if (hasWrite) {
                accessType = "write";
            }

            await saveTableAccess({
                workflowId,
                componentId: table.id,
                accessType,
                requiresLogin: true,
                securityFilter: mongoQuery,
            });

            // Reload table access to get the updated data with proper table information
            await loadTableAccess();

            // Clear editing state
            cancelEditing(table.id);

            toast.success(
                t("dataSecurity.rulesSaved", { tableName: table.name }) ||
                    `Access rules saved for ${table.name}`
            );
        } catch (error) {
            console.error("Error saving rules:", error);
            const serverMessage =
                error?.response?.data?.error || error?.message;
            toast.error(
                serverMessage ||
                    t("dataSecurity.saveError") ||
                    "Failed to save access rules"
            );
        }
    };

    // Render rules for a specific operation
    const renderRulesForOperation = (table, operation, rules, isEditing) => {
        const tableEntityFields = entityFields[table.id] || [];
        if (rules.length === 0 && !isEditing) {
            const message =
                operation === "read"
                    ? "Anyone logged in can view this data"
                    : `Nobody can ${operation} this data`;

            return (
                <div className="text-center py-8 px-4">
                    <p className="text-sm text-gray-600 mb-2">
                        {t("dataSecurity.noRulesForOperation") || message}
                    </p>
                    <p className="text-xs text-gray-500">
                        {operation === "read"
                            ? "Click Edit to add restrictions"
                            : "Click Edit to allow users to perform this action"}
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                {rules.length === 0 && isEditing && (
                    <div className="text-center py-6 px-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">
                            No access rules defined yet
                        </p>
                        <p className="text-xs text-gray-500">
                            Click "Add Access Rule" below to create your first
                            rule
                        </p>
                    </div>
                )}
                {rules.map((rule, index) => (
                    <div
                        key={rule.id}
                        className={`border rounded-lg p-4 space-y-3 bg-white ${
                            index === 0 ? "ring-1 ring-blue-100" : ""
                        }`}
                    >
                        {/* First rule indicator */}
                        {index === 0 && isEditing && (
                            <div className="flex items-center gap-1.5 -mt-1 mb-2">
                                <span className="text-[10px] uppercase tracking-wide text-blue-600 font-medium">
                                    {t("dataSecurity.baseRule") || "Base rule"}
                                </span>
                            </div>
                        )}
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 space-y-3">
                                {/* Rule Type - Sentence format */}
                                <div>
                                    <Label className="text-xs text-gray-500 mb-1.5 block">
                                        {t("dataSecurity.allowAccessIf") ||
                                            "Allow access if..."}
                                    </Label>
                                    <Select
                                        value={rule.type}
                                        onValueChange={(value) =>
                                            updateRule(
                                                table.id,
                                                operation,
                                                rule.id,
                                                {
                                                    type: value,
                                                    config: {
                                                        matchType: "value",
                                                    },
                                                }
                                            )
                                        }
                                        disabled={!isEditing}
                                    >
                                        <SelectTrigger className="h-9">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="creator">
                                                {t(
                                                    "dataSecurity.ruleCreator"
                                                ) || "User created this record"}
                                            </SelectItem>
                                            <SelectItem value="authenticated">
                                                {t(
                                                    "dataSecurity.ruleAuthenticated"
                                                ) || "User is logged in"}
                                            </SelectItem>
                                            <SelectItem value="userField">
                                                {t(
                                                    "dataSecurity.ruleUserField"
                                                ) ||
                                                    "User has specific property"}
                                            </SelectItem>
                                            <SelectItem value="entityField">
                                                {t(
                                                    "dataSecurity.ruleEntityField"
                                                ) ||
                                                    "Record has specific value"}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Read-only display of rule configuration when not editing */}
                                {!isEditing &&
                                    rule.type === "authenticated" && (
                                        <div className="text-sm text-gray-700 bg-gray-50 rounded p-3 mt-2">
                                            <span>
                                                {t(
                                                    "dataSecurity.authenticatedRuleDescription"
                                                ) || "User must be logged in"}
                                            </span>
                                        </div>
                                    )}

                                {!isEditing && rule.type === "userField" && (
                                    <div className="text-sm text-gray-700 bg-gray-50 rounded p-3 mt-2">
                                        {rule.config.matchType === "value" ? (
                                            <span>
                                                User's{" "}
                                                <code className="bg-gray-200 px-1 rounded">
                                                    {rule.config.userField}
                                                </code>
                                                {" = "}
                                                <code className="bg-gray-200 px-1 rounded">
                                                    "{rule.config.matchValue}"
                                                </code>
                                            </span>
                                        ) : (
                                            <span>
                                                Record's{" "}
                                                <code className="bg-gray-200 px-1 rounded">
                                                    {rule.config.entityField}
                                                </code>
                                                {" = User's "}
                                                <code className="bg-gray-200 px-1 rounded">
                                                    {rule.config.userField}
                                                </code>
                                            </span>
                                        )}
                                    </div>
                                )}

                                {!isEditing && rule.type === "entityField" && (
                                    <div className="text-sm text-gray-700 bg-gray-50 rounded p-3 mt-2">
                                        <span>
                                            Record's{" "}
                                            <code className="bg-gray-200 px-1 rounded">
                                                {rule.config.fieldName}
                                            </code>
                                            {" = "}
                                            <code className="bg-gray-200 px-1 rounded">
                                                "{rule.config.fieldValue}"
                                            </code>
                                        </span>
                                    </div>
                                )}

                                {!isEditing && rule.type === "creator" && (
                                    <div className="text-sm text-gray-700 bg-gray-50 rounded p-3 mt-2">
                                        <span>
                                            Record's{" "}
                                            <code className="bg-gray-200 px-1 rounded">
                                                createdBy
                                            </code>
                                            {" = User's "}
                                            <code className="bg-gray-200 px-1 rounded">
                                                email
                                            </code>
                                        </span>
                                    </div>
                                )}

                                {/* Rule Configuration - User Field Match */}
                                {isEditing && rule.type === "userField" && (
                                    <div className="space-y-3">
                                        {/* Match Type Selection */}
                                        <div>
                                            <Label className="text-xs text-gray-500 mb-1.5 block">
                                                {t("dataSecurity.compareHow") ||
                                                    "How to compare?"}
                                            </Label>
                                            <Select
                                                value={
                                                    rule.config.matchType ||
                                                    "value"
                                                }
                                                onValueChange={(value) =>
                                                    updateRule(
                                                        table.id,
                                                        operation,
                                                        rule.id,
                                                        {
                                                            config: {
                                                                ...rule.config,
                                                                matchType:
                                                                    value,
                                                                // Clear irrelevant fields
                                                                ...(value ===
                                                                "value"
                                                                    ? {
                                                                          entityField:
                                                                              undefined,
                                                                      }
                                                                    : {
                                                                          matchValue:
                                                                              undefined,
                                                                      }),
                                                            },
                                                        }
                                                    )
                                                }
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="value">
                                                        {t(
                                                            "dataSecurity.matchTypeValue"
                                                        ) ||
                                                            "User property equals specific value"}
                                                    </SelectItem>
                                                    <SelectItem value="field">
                                                        {t(
                                                            "dataSecurity.matchTypeField"
                                                        ) ||
                                                            "User property matches record field"}
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* User Field Selection - Only for value-based matching */}
                                        {rule.config.matchType === "value" && (
                                            <div>
                                                <Label className="text-xs text-gray-500 mb-1.5 block">
                                                    {t(
                                                        "dataSecurity.userPropertyLabel"
                                                    ) || "User property"}
                                                </Label>
                                                <Select
                                                    value={
                                                        rule.config.userField ||
                                                        ""
                                                    }
                                                    onValueChange={(value) =>
                                                        updateRule(
                                                            table.id,
                                                            operation,
                                                            rule.id,
                                                            {
                                                                config: {
                                                                    ...rule.config,
                                                                    userField:
                                                                        value,
                                                                },
                                                            }
                                                        )
                                                    }
                                                >
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue
                                                            placeholder={
                                                                t(
                                                                    "dataSecurity.selectProperty"
                                                                ) ||
                                                                "Select property"
                                                            }
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {userFields.map(
                                                            (field) => {
                                                                // Human-readable labels for known fields
                                                                const knownLabels =
                                                                    {
                                                                        role:
                                                                            t(
                                                                                "dataSecurity.fieldRole"
                                                                            ) ||
                                                                            "Role",
                                                                        email:
                                                                            t(
                                                                                "dataSecurity.fieldEmail"
                                                                            ) ||
                                                                            "Email",
                                                                        userId:
                                                                            t(
                                                                                "dataSecurity.fieldUserId"
                                                                            ) ||
                                                                            "User ID",
                                                                    };
                                                                const label =
                                                                    knownLabels[
                                                                        field
                                                                    ];
                                                                return (
                                                                    <SelectItem
                                                                        key={
                                                                            field
                                                                        }
                                                                        value={
                                                                            field
                                                                        }
                                                                    >
                                                                        {label ? (
                                                                            <div className="flex flex-col">
                                                                                <span>
                                                                                    {
                                                                                        label
                                                                                    }
                                                                                </span>
                                                                                <span className="text-[10px] text-gray-400">
                                                                                    {
                                                                                        field
                                                                                    }
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <span>
                                                                                {
                                                                                    field
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </SelectItem>
                                                                );
                                                            }
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Value-based matching */}
                                        {rule.config.matchType === "value" &&
                                            rule.config.userField && (
                                                <div className="space-y-2">
                                                    <Label className="text-xs text-gray-500 mb-1.5 block">
                                                        {t(
                                                            "dataSecurity.mustEqual"
                                                        ) || "Must equal"}
                                                    </Label>
                                                    {rule.config.userField ===
                                                    "role" ? (
                                                        // Role dropdown
                                                        <Select
                                                            value={
                                                                rule.config
                                                                    .matchValue ||
                                                                ""
                                                            }
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                updateRule(
                                                                    table.id,
                                                                    operation,
                                                                    rule.id,
                                                                    {
                                                                        config: {
                                                                            ...rule.config,
                                                                            matchValue:
                                                                                value,
                                                                        },
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue
                                                                    placeholder={
                                                                        t(
                                                                            "dataSecurity.selectRole"
                                                                        ) ||
                                                                        "Select role"
                                                                    }
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="admin">
                                                                    {t(
                                                                        "dataSecurity.roleAdmin"
                                                                    ) ||
                                                                        "Administrator"}
                                                                </SelectItem>
                                                                <SelectItem value="user">
                                                                    {t(
                                                                        "dataSecurity.roleUser"
                                                                    ) ||
                                                                        "Regular user"}
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    ) : (
                                                        // Text input for other fields
                                                        <Input
                                                            className="h-9"
                                                            placeholder={
                                                                t(
                                                                    "dataSecurity.enterValue"
                                                                ) ||
                                                                "Enter value"
                                                            }
                                                            value={
                                                                rule.config
                                                                    .matchValue ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                updateRule(
                                                                    table.id,
                                                                    operation,
                                                                    rule.id,
                                                                    {
                                                                        config: {
                                                                            ...rule.config,
                                                                            matchValue:
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                        },
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {/* Show summary if both field and value are selected */}
                                                    {rule.config.matchValue && (
                                                        <div className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded p-2">
                                                            {t(
                                                                "dataSecurity.ruleSummaryValue",
                                                                {
                                                                    field: rule
                                                                        .config
                                                                        .userField,
                                                                    value: rule
                                                                        .config
                                                                        .matchValue,
                                                                }
                                                            ) ||
                                                                `Users with ${rule.config.userField} = "${rule.config.matchValue}" can access`}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                        {/* Field-based matching */}
                                        {rule.config.matchType === "field" && (
                                            <div className="space-y-3">
                                                <div className="flex items-end gap-2">
                                                    {/* Record Field - from table */}
                                                    <div className="flex-1">
                                                        <Label className="text-xs text-gray-500 mb-1.5 block">
                                                            {t(
                                                                "dataSecurity.recordField"
                                                            ) || "Record field"}
                                                        </Label>
                                                        <Select
                                                            value={
                                                                rule.config
                                                                    .entityField ||
                                                                ""
                                                            }
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                updateRule(
                                                                    table.id,
                                                                    operation,
                                                                    rule.id,
                                                                    {
                                                                        config: {
                                                                            ...rule.config,
                                                                            entityField:
                                                                                value,
                                                                        },
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue
                                                                    placeholder={
                                                                        t(
                                                                            "dataSecurity.selectField"
                                                                        ) ||
                                                                        "Select field"
                                                                    }
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {tableEntityFields.length >
                                                                0 ? (
                                                                    tableEntityFields.map(
                                                                        (
                                                                            field
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    field
                                                                                }
                                                                                value={
                                                                                    field
                                                                                }
                                                                            >
                                                                                {
                                                                                    field
                                                                                }
                                                                            </SelectItem>
                                                                        )
                                                                    )
                                                                ) : (
                                                                    <div className="px-2 py-1.5 text-sm text-gray-500">
                                                                        {t(
                                                                            "dataSecurity.noFields"
                                                                        ) ||
                                                                            "No fields available"}
                                                                    </div>
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div className="flex items-center justify-center text-xs text-gray-400 pb-2">
                                                        =
                                                    </div>

                                                    {/* User Field - to compare with */}
                                                    <div className="flex-1">
                                                        <Label className="text-xs text-gray-500 mb-1.5 block">
                                                            {t(
                                                                "dataSecurity.userPropertyLabel"
                                                            ) ||
                                                                "User property"}
                                                        </Label>
                                                        <Select
                                                            value={
                                                                rule.config
                                                                    .userField ||
                                                                ""
                                                            }
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                updateRule(
                                                                    table.id,
                                                                    operation,
                                                                    rule.id,
                                                                    {
                                                                        config: {
                                                                            ...rule.config,
                                                                            userField:
                                                                                value,
                                                                        },
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue
                                                                    placeholder={
                                                                        t(
                                                                            "dataSecurity.selectProperty"
                                                                        ) ||
                                                                        "Select property"
                                                                    }
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {userFields.map(
                                                                    (field) => (
                                                                        <SelectItem
                                                                            key={
                                                                                field
                                                                            }
                                                                            value={
                                                                                field
                                                                            }
                                                                        >
                                                                            {
                                                                                field
                                                                            }
                                                                        </SelectItem>
                                                                    )
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                {/* Show summary if both fields are selected */}
                                                {rule.config.entityField &&
                                                    rule.config.userField && (
                                                        <div className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded p-2">
                                                            {t(
                                                                "dataSecurity.ruleSummaryField",
                                                                {
                                                                    recordField:
                                                                        rule
                                                                            .config
                                                                            .entityField,
                                                                    userField:
                                                                        rule
                                                                            .config
                                                                            .userField,
                                                                }
                                                            ) ||
                                                                `Access allowed when record's "${rule.config.entityField}" matches user's "${rule.config.userField}"`}
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Rule Configuration - Entity Field Match */}
                                {isEditing && rule.type === "entityField" && (
                                    <div className="space-y-3">
                                        <div className="flex items-end gap-2">
                                            {/* Field Selection */}
                                            <div className="flex-1">
                                                <Label className="text-xs text-gray-500 mb-1.5 block">
                                                    {t(
                                                        "dataSecurity.recordField"
                                                    ) || "Record field"}
                                                </Label>
                                                <Select
                                                    value={
                                                        rule.config.fieldName ||
                                                        ""
                                                    }
                                                    onValueChange={(value) =>
                                                        updateRule(
                                                            table.id,
                                                            operation,
                                                            rule.id,
                                                            {
                                                                config: {
                                                                    ...rule.config,
                                                                    fieldName:
                                                                        value,
                                                                },
                                                            }
                                                        )
                                                    }
                                                >
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue
                                                            placeholder={
                                                                t(
                                                                    "dataSecurity.selectField"
                                                                ) ||
                                                                "Select field"
                                                            }
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {tableEntityFields.length >
                                                        0 ? (
                                                            tableEntityFields.map(
                                                                (field) => (
                                                                    <SelectItem
                                                                        key={
                                                                            field
                                                                        }
                                                                        value={
                                                                            field
                                                                        }
                                                                    >
                                                                        {field}
                                                                    </SelectItem>
                                                                )
                                                            )
                                                        ) : (
                                                            <div className="px-2 py-1.5 text-sm text-gray-500">
                                                                {t(
                                                                    "dataSecurity.noFields"
                                                                ) ||
                                                                    "No fields available"}
                                                            </div>
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="flex items-center justify-center text-xs text-gray-400 pb-2">
                                                =
                                            </div>

                                            {/* Value Input */}
                                            <div className="flex-1">
                                                <Label className="text-xs text-gray-500 mb-1.5 block">
                                                    {t(
                                                        "dataSecurity.mustEqual"
                                                    ) || "Must equal"}
                                                </Label>
                                                <Input
                                                    className="h-9"
                                                    placeholder={
                                                        t(
                                                            "dataSecurity.exampleOpen"
                                                        ) || "e.g., active"
                                                    }
                                                    value={
                                                        rule.config
                                                            .fieldValue || ""
                                                    }
                                                    onChange={(e) =>
                                                        updateRule(
                                                            table.id,
                                                            operation,
                                                            rule.id,
                                                            {
                                                                config: {
                                                                    ...rule.config,
                                                                    fieldValue:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            }
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>

                                        {/* Show summary if both field and value are selected */}
                                        {rule.config.fieldName &&
                                            rule.config.fieldValue && (
                                                <div className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded p-2">
                                                    {t(
                                                        "dataSecurity.ruleSummaryEntity",
                                                        {
                                                            field: rule.config
                                                                .fieldName,
                                                            value: rule.config
                                                                .fieldValue,
                                                        }
                                                    ) ||
                                                        `Only records where "${rule.config.fieldName}" = "${rule.config.fieldValue}"`}
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>

                            {isEditing && (
                                <button
                                    type="button"
                                    className="text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1"
                                    onClick={() =>
                                        removeRule(table.id, operation, rule.id)
                                    }
                                >
                                    {t("dataSecurity.removeRule") || "Remove"}
                                </button>
                            )}
                        </div>

                        {index < rules.length - 1 && (
                            <div className="text-center py-1">
                                {isEditing ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            toggleOperator(table.id, operation)
                                        }
                                        className="text-[10px] uppercase tracking-wide px-3 py-1 rounded-full border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer"
                                    >
                                        <span
                                            className={`font-medium ${
                                                (ruleOperators[table.id]?.[
                                                    operation
                                                ] || "or") === "and"
                                                    ? "text-blue-600"
                                                    : "text-gray-500"
                                            }`}
                                        >
                                            {(ruleOperators[table.id]?.[
                                                operation
                                            ] || "or") === "and"
                                                ? t(
                                                      "dataSecurity.andOperator"
                                                  ) || "AND"
                                                : t(
                                                      "dataSecurity.orOperator"
                                                  ) || "OR"}
                                        </span>
                                        <span className="text-gray-400 ml-1">
                                            {(ruleOperators[table.id]?.[
                                                operation
                                            ] || "or") === "and"
                                                ? t("dataSecurity.andHint") ||
                                                  "- all must match"
                                                : t("dataSecurity.orHint") ||
                                                  "- any can match"}
                                        </span>
                                    </button>
                                ) : (
                                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                                        {(ruleOperators[table.id]?.[
                                            operation
                                        ] || "or") === "and"
                                            ? t("dataSecurity.andOperator") ||
                                              "AND"
                                            : t("dataSecurity.orOperator") ||
                                              "OR"}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {isEditing && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addRule(table.id, operation)}
                        className="w-full"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        {t("dataSecurity.addRule") || "Add Access Rule"}
                    </Button>
                )}
            </div>
        );
    };

    // Use entities instead of database tables
    // Entities are fetched directly from components

    // Check if current environment is not production
    const isNotProduction = useMemo(() => {
        return (
            selectedEnvironment &&
            selectedEnvironment !== "production" &&
            selectedEnvironment !== "prod"
        );
    }, [selectedEnvironment]);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="px-6 py-5 border-b bg-white flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900">
                    {t("dataSecurity.title") || "Who can access this data?"}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    {t("dataSecurity.subtitle") ||
                        "Choose who can access this table. Advanced permissions can be adjusted later."}
                </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {isNotProduction && (
                    <Alert className="mb-4 bg-yellow-50 border-yellow-200">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertTitle className="text-yellow-800">
                            {t("dataSecurity.nonProductionWarning") ||
                                "Non-Production Environment"}
                        </AlertTitle>
                        <AlertDescription className="text-yellow-700">
                            {t("dataSecurity.nonProductionMessage") ||
                                "Changes to this section will affect production."}
                        </AlertDescription>
                    </Alert>
                )}
                {entities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
                        <p className="text-gray-600 text-sm">
                            {t("dataSecurity.noTables") ||
                                "No data entities found. Create tables in your database to manage access rules."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {entities.map((entity) => {
                            // Use entity as table for consistency with existing code
                            const table = entity;
                            const isEditing = !!editingRules[table.id];

                            // Find the access record for this entity
                            const entityAccess = findAccessForEntity(entity);

                            const rulesByOperation = isEditing
                                ? editingRules[table.id]
                                : parsedRulesByEntity[table.id]?.rules || {
                                      read: [],
                                      create: [],
                                      update: [],
                                      delete: [],
                                  };
                            const isExpanded = expandedTables[table.id];
                            const currentTab = activeTab[table.id] || "read";

                            // Count total rules
                            const totalRules =
                                (rulesByOperation.read?.length || 0) +
                                (rulesByOperation.create?.length || 0) +
                                (rulesByOperation.update?.length || 0) +
                                (rulesByOperation.delete?.length || 0);

                            // Check if table has public access (no rules)
                            const isPublic = totalRules === 0;

                            return (
                                <Card
                                    key={table.id}
                                    className="border-gray-100 shadow-sm"
                                >
                                    <CardHeader
                                        className="cursor-pointer hover:bg-gray-50/50 py-4"
                                        onClick={() => toggleTable(table.id)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                {isExpanded ? (
                                                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-400 uppercase tracking-wide">
                                                            {t(
                                                                "dataSecurity.table"
                                                            ) || "Table"}
                                                            :
                                                        </span>
                                                        <CardTitle className="text-base font-medium">
                                                            {table.name.replace(
                                                                ".json",
                                                                ""
                                                            )}
                                                        </CardTitle>
                                                    </div>
                                                    {/* Status indicator with alert for public */}
                                                    {isPublic &&
                                                    !requiresLogin ? (
                                                        <div className="flex items-center gap-1.5 mt-1">
                                                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                                            <span className="text-xs text-amber-600 font-medium">
                                                                {t(
                                                                    "dataSecurity.statusPublicWarning"
                                                                ) ||
                                                                    "Public - Anyone can access"}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <CardDescription className="text-xs mt-0.5 text-gray-500">
                                                            {isPublic &&
                                                            requiresLogin
                                                                ? t(
                                                                      "dataSecurity.statusSignedIn"
                                                                  ) ||
                                                                  "Signed-in users"
                                                                : t(
                                                                      "dataSecurity.statusConfigured"
                                                                  ) ||
                                                                  "Access configured"}
                                                        </CardDescription>
                                                    )}
                                                </div>
                                            </div>
                                            {!isEditing && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="text-gray-500 hover:text-gray-700"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        startEditing(table);
                                                        setExpandedTables(
                                                            (prev) => ({
                                                                ...prev,
                                                                [table.id]: true,
                                                            })
                                                        );
                                                    }}
                                                >
                                                    {t("common.edit") || "Edit"}
                                                </Button>
                                            )}
                                        </div>
                                    </CardHeader>

                                    {isExpanded && (
                                        <CardContent className="space-y-5 pt-2">
                                            {/* Template Selection - Only show when editing */}
                                            {isEditing && (
                                                <div className="space-y-5">
                                                    {/* Main access options - grouped together */}
                                                    <div className="space-y-2">
                                                        {templates
                                                            .filter(
                                                                (template) =>
                                                                    template.id !==
                                                                        "custom" &&
                                                                    (template.requiresLoginOnly ===
                                                                        undefined ||
                                                                        template.requiresLoginOnly ===
                                                                            requiresLogin)
                                                            )
                                                            .map((template) => {
                                                                const IconComponent =
                                                                    template.icon;
                                                                const isSelected =
                                                                    selectedTemplate[
                                                                        table.id
                                                                    ] ===
                                                                    template.id;
                                                                return (
                                                                    <button
                                                                        key={
                                                                            template.id
                                                                        }
                                                                        onClick={() =>
                                                                            applyTemplate(
                                                                                table.id,
                                                                                template.id
                                                                            )
                                                                        }
                                                                        className={`
                                                                        flex items-center gap-3 p-3 rounded-lg transition-all text-left w-full
                                                                        ${
                                                                            isSelected
                                                                                ? "bg-blue-50 ring-1 ring-blue-200"
                                                                                : "hover:bg-gray-50"
                                                                        }
                                                                    `}
                                                                    >
                                                                        <div
                                                                            className={`
                                                                        w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                                                                        ${
                                                                            isSelected
                                                                                ? "bg-blue-100 text-blue-600"
                                                                                : "bg-gray-100 text-gray-500"
                                                                        }
                                                                    `}
                                                                        >
                                                                            <IconComponent className="w-4 h-4" />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <div
                                                                                className={`text-sm ${
                                                                                    isSelected
                                                                                        ? "font-medium text-blue-900"
                                                                                        : "text-gray-700"
                                                                                }`}
                                                                            >
                                                                                {
                                                                                    template.title
                                                                                }
                                                                            </div>
                                                                            <div className="text-xs text-gray-400 mt-0.5">
                                                                                {
                                                                                    template.description
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                        {isSelected && (
                                                                            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                                                <svg
                                                                                    className="w-3 h-3 text-white"
                                                                                    fill="none"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    strokeWidth="2"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                >
                                                                                    <path d="M5 13l4 4L19 7"></path>
                                                                                </svg>
                                                                            </div>
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                    </div>

                                                    {/* Public access alert - important warning */}
                                                    {selectedTemplate[
                                                        table.id
                                                    ] === "public" && (
                                                        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                                                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                            <div>
                                                                <div className="font-medium text-sm text-amber-900">
                                                                    {t(
                                                                        "dataSecurity.publicWarning.title"
                                                                    ) ||
                                                                        "Public access"}
                                                                </div>
                                                                <div className="text-xs text-amber-700 mt-1">
                                                                    {t(
                                                                        "dataSecurity.publicWarning.description"
                                                                    ) ||
                                                                        "Anyone on the internet can access this data without logging in."}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Live summary */}
                                                    {selectedTemplate[
                                                        table.id
                                                    ] && (
                                                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                                                            <div className="text-xs font-medium text-gray-500 mb-2">
                                                                {t(
                                                                    "dataSecurity.summary"
                                                                ) || "Summary"}
                                                            </div>
                                                            <div className="text-sm text-gray-700 space-y-1">
                                                                {selectedTemplate[
                                                                    table.id
                                                                ] ===
                                                                    "only-mine" && (
                                                                    <>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summaryOnlyMine1"
                                                                            ) ||
                                                                                "Only the creator can access"}
                                                                        </div>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summaryOnlyMine2"
                                                                            ) ||
                                                                                "Other users cannot see this data"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {selectedTemplate[
                                                                    table.id
                                                                ] ===
                                                                    "admin-only" && (
                                                                    <>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summaryAdmin1"
                                                                            ) ||
                                                                                "Only admins have access"}
                                                                        </div>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summaryAdmin2"
                                                                            ) ||
                                                                                "Regular users are blocked"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {(selectedTemplate[
                                                                    table.id
                                                                ] ===
                                                                    "everyone-logged-in" ||
                                                                    selectedTemplate[
                                                                        table.id
                                                                    ] ===
                                                                        "authenticated") && (
                                                                    <>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summarySignedIn1"
                                                                            ) ||
                                                                                "Any signed-in user can access"}
                                                                        </div>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summarySignedIn2"
                                                                            ) ||
                                                                                "Anonymous users are blocked"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {selectedTemplate[
                                                                    table.id
                                                                ] ===
                                                                    "public" && (
                                                                    <>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summaryPublic1"
                                                                            ) ||
                                                                                "Anyone can view this data"}
                                                                        </div>
                                                                        <div>
                                                                            •{" "}
                                                                            {t(
                                                                                "dataSecurity.summaryPublic2"
                                                                            ) ||
                                                                                "No login required"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {selectedTemplate[
                                                                    table.id
                                                                ] ===
                                                                    "custom" && (
                                                                    <div>
                                                                        •{" "}
                                                                        {t(
                                                                            "dataSecurity.summaryCustom"
                                                                        ) ||
                                                                            "Custom rules configured"}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Custom rules - separated and de-emphasized */}
                                                    {/* Custom rules link - de-emphasized */}
                                                    {!showAdvanced[
                                                        table.id
                                                    ] && (
                                                        <div className="pt-3 border-t border-gray-100">
                                                            <button
                                                                onClick={() => {
                                                                    applyTemplate(
                                                                        table.id,
                                                                        "custom"
                                                                    );
                                                                    setShowAdvanced(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [table.id]: true,
                                                                        })
                                                                    );
                                                                }}
                                                                className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                                            >
                                                                <Settings className="w-3 h-3" />
                                                                {t(
                                                                    "dataSecurity.needMoreControl"
                                                                ) ||
                                                                    "Need more control? Set custom rules"}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Advanced Rules Editor - Show when in custom mode or customize is clicked */}
                                            {isEditing &&
                                                (selectedTemplate[table.id] ===
                                                    "custom" ||
                                                    showAdvanced[table.id]) && (
                                                    <div className="border-t pt-4">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div>
                                                                <Label className="text-sm font-medium text-gray-700">
                                                                    {t(
                                                                        "dataSecurity.advancedRulesTitle"
                                                                    ) ||
                                                                        "Advanced rules"}
                                                                    <span className="text-xs font-normal text-gray-400 ml-2">
                                                                        {t(
                                                                            "dataSecurity.advancedRulesSubtitle"
                                                                        ) ||
                                                                            "(for complex cases)"}
                                                                    </span>
                                                                </Label>
                                                                <p className="text-xs text-gray-400 mt-0.5">
                                                                    {t(
                                                                        "dataSecurity.advancedRulesHint"
                                                                    ) ||
                                                                        "Use this only if the default access options aren't enough."}
                                                                </p>
                                                            </div>
                                                            {showAdvanced[
                                                                table.id
                                                            ] &&
                                                                selectedTemplate[
                                                                    table.id
                                                                ] !==
                                                                    "custom" && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        className="text-gray-400 hover:text-gray-600"
                                                                        onClick={() =>
                                                                            setShowAdvanced(
                                                                                (
                                                                                    prev
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    [table.id]: false,
                                                                                })
                                                                            )
                                                                        }
                                                                    >
                                                                        {t(
                                                                            "common.close"
                                                                        ) ||
                                                                            "Hide"}
                                                                    </Button>
                                                                )}
                                                        </div>
                                                        {/* How rules work - subtle info */}
                                                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mb-3">
                                                            <Info className="w-3 h-3" />
                                                            <span>
                                                                {t(
                                                                    "dataSecurity.howRulesWork"
                                                                ) ||
                                                                    "If any rule matches, access is granted."}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Tabs - Always show but rules are controlled by template/advanced mode */}
                                            {(!isEditing ||
                                                selectedTemplate[table.id] ===
                                                    "custom" ||
                                                showAdvanced[table.id]) && (
                                                <Tabs
                                                    value={currentTab}
                                                    onValueChange={(value) =>
                                                        setActiveTab(
                                                            (prev) => ({
                                                                ...prev,
                                                                [table.id]:
                                                                    value,
                                                            })
                                                        )
                                                    }
                                                >
                                                    <div className="space-y-2">
                                                        <TabsList className="grid w-full grid-cols-5">
                                                            <TabsTrigger
                                                                value="read"
                                                                className="text-xs"
                                                            >
                                                                {t(
                                                                    "dataSecurity.tabs.read"
                                                                ) || "Read"}
                                                                {rulesByOperation
                                                                    .read
                                                                    ?.length >
                                                                    0 && (
                                                                    <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                                                        {
                                                                            rulesByOperation
                                                                                .read
                                                                                .length
                                                                        }
                                                                    </span>
                                                                )}
                                                            </TabsTrigger>
                                                            <TabsTrigger
                                                                value="create"
                                                                className="text-xs"
                                                            >
                                                                {t(
                                                                    "dataSecurity.tabs.create"
                                                                ) || "Create"}
                                                                {rulesByOperation
                                                                    .create
                                                                    ?.length >
                                                                    0 && (
                                                                    <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                                                        {
                                                                            rulesByOperation
                                                                                .create
                                                                                .length
                                                                        }
                                                                    </span>
                                                                )}
                                                            </TabsTrigger>
                                                            <TabsTrigger
                                                                value="update"
                                                                className="text-xs"
                                                            >
                                                                {t(
                                                                    "dataSecurity.tabs.update"
                                                                ) || "Update"}
                                                                {rulesByOperation
                                                                    .update
                                                                    ?.length >
                                                                    0 && (
                                                                    <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                                                        {
                                                                            rulesByOperation
                                                                                .update
                                                                                .length
                                                                        }
                                                                    </span>
                                                                )}
                                                            </TabsTrigger>
                                                            <TabsTrigger
                                                                value="delete"
                                                                className="text-xs"
                                                            >
                                                                {t(
                                                                    "dataSecurity.tabs.delete"
                                                                ) || "Delete"}
                                                                {rulesByOperation
                                                                    .delete
                                                                    ?.length >
                                                                    0 && (
                                                                    <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                                                        {
                                                                            rulesByOperation
                                                                                .delete
                                                                                .length
                                                                        }
                                                                    </span>
                                                                )}
                                                            </TabsTrigger>
                                                            <TabsTrigger
                                                                value="masking"
                                                                className="text-xs"
                                                            >
                                                                {t(
                                                                    "dataSecurity.tabs.masking"
                                                                ) || "Masking"}
                                                                {Object.keys(
                                                                    obfuscationConfigs[
                                                                        table.id
                                                                    ]?.fields ||
                                                                        {}
                                                                ).length >
                                                                    0 && (
                                                                    <span className="ml-1 bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                                                        {
                                                                            Object.keys(
                                                                                obfuscationConfigs[
                                                                                    table
                                                                                        .id
                                                                                ]
                                                                                    .fields
                                                                            )
                                                                                .length
                                                                        }
                                                                    </span>
                                                                )}
                                                            </TabsTrigger>
                                                        </TabsList>
                                                        {isEditing && (
                                                            <p className="text-[11px] text-gray-400 text-center">
                                                                {t(
                                                                    "dataSecurity.tabsHint"
                                                                ) ||
                                                                    "Configure each action only if you need different rules."}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <TabsContent
                                                        value="read"
                                                        className="mt-4"
                                                    >
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {t(
                                                                "dataSecurity.tabs.readHelp"
                                                            ) ||
                                                                "Who can see records in this table?"}
                                                        </p>
                                                        {renderRulesForOperation(
                                                            table,
                                                            "read",
                                                            rulesByOperation.read ||
                                                                [],
                                                            isEditing
                                                        )}
                                                    </TabsContent>

                                                    <TabsContent
                                                        value="create"
                                                        className="mt-4"
                                                    >
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {t(
                                                                "dataSecurity.tabs.createHelp"
                                                            ) ||
                                                                "Who can create new entries?"}
                                                        </p>
                                                        {renderRulesForOperation(
                                                            table,
                                                            "create",
                                                            rulesByOperation.create ||
                                                                [],
                                                            isEditing
                                                        )}
                                                    </TabsContent>

                                                    <TabsContent
                                                        value="update"
                                                        className="mt-4"
                                                    >
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {t(
                                                                "dataSecurity.tabs.updateHelp"
                                                            ) ||
                                                                "Who can modify existing records?"}
                                                        </p>
                                                        {renderRulesForOperation(
                                                            table,
                                                            "update",
                                                            rulesByOperation.update ||
                                                                [],
                                                            isEditing
                                                        )}
                                                    </TabsContent>

                                                    <TabsContent
                                                        value="delete"
                                                        className="mt-4"
                                                    >
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {t(
                                                                "dataSecurity.tabs.deleteHelp"
                                                            ) ||
                                                                "Who can delete records?"}
                                                        </p>
                                                        {renderRulesForOperation(
                                                            table,
                                                            "delete",
                                                            rulesByOperation.delete ||
                                                                [],
                                                            isEditing
                                                        )}
                                                    </TabsContent>

                                                    <TabsContent
                                                        value="masking"
                                                        className="mt-4"
                                                    >
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {t(
                                                                "dataSecurity.tabs.maskingHelp"
                                                            ) ||
                                                                "Which fields should be masked for unauthorized users?"}
                                                        </p>
                                                        {canModifyObfuscation ? (
                                                            <ObfuscationConfigPanel
                                                                isEditing={
                                                                    isEditing
                                                                }
                                                                entityFields={
                                                                    entityFields[
                                                                        table.id
                                                                    ] || []
                                                                }
                                                                userFields={
                                                                    userFields
                                                                }
                                                                config={
                                                                    obfuscationConfigs[
                                                                        table.id
                                                                    ] || {
                                                                        fields: {},
                                                                        rawAccessRules:
                                                                            [],
                                                                    }
                                                                }
                                                                onChange={(
                                                                    cfg
                                                                ) =>
                                                                    setObfuscationConfigs(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [table.id]:
                                                                                cfg,
                                                                        })
                                                                    )
                                                                }
                                                                isEnterprise={
                                                                    true
                                                                }
                                                            />
                                                        ) : (
                                                            <Alert>
                                                                <Lock className="h-4 w-4" />
                                                                <AlertDescription>
                                                                    {t(
                                                                        "dataSecurity.cannotModifyObfuscation"
                                                                    )}
                                                                </AlertDescription>
                                                            </Alert>
                                                        )}
                                                    </TabsContent>
                                                </Tabs>
                                            )}

                                            {/* Live Access Summary - Critical */}
                                            {isEditing &&
                                                (selectedTemplate[table.id] ===
                                                    "custom" ||
                                                    showAdvanced[table.id]) && (
                                                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                                                        <div className="text-xs font-medium text-gray-500 mb-2">
                                                            {t(
                                                                "dataSecurity.accessSummary"
                                                            ) ||
                                                                "Access summary"}
                                                        </div>
                                                        <div className="text-sm text-gray-700 space-y-1">
                                                            {rulesByOperation
                                                                .read?.length >
                                                                0 &&
                                                                rulesByOperation.read.map(
                                                                    (
                                                                        rule,
                                                                        idx
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                idx
                                                                            }
                                                                            className="flex items-start gap-2"
                                                                        >
                                                                            <span className="text-gray-400">
                                                                                •
                                                                            </span>
                                                                            <span>
                                                                                {rule.type ===
                                                                                    "creator" &&
                                                                                    (t(
                                                                                        "dataSecurity.summaryRuleCreator"
                                                                                    ) ||
                                                                                        "The creator can always view records")}
                                                                                {rule.type ===
                                                                                    "authenticated" &&
                                                                                    (t(
                                                                                        "dataSecurity.summaryRuleAuth"
                                                                                    ) ||
                                                                                        "Any logged-in user can access")}
                                                                                {rule.type ===
                                                                                    "userField" &&
                                                                                    rule
                                                                                        .config
                                                                                        .matchType ===
                                                                                        "value" &&
                                                                                    (t(
                                                                                        "dataSecurity.summaryRuleUserValue",
                                                                                        {
                                                                                            field: rule
                                                                                                .config
                                                                                                .userField,
                                                                                            value: rule
                                                                                                .config
                                                                                                .matchValue,
                                                                                        }
                                                                                    ) ||
                                                                                        `Users with ${rule.config.userField} = "${rule.config.matchValue}" can access`)}
                                                                                {rule.type ===
                                                                                    "userField" &&
                                                                                    rule
                                                                                        .config
                                                                                        .matchType ===
                                                                                        "field" &&
                                                                                    (t(
                                                                                        "dataSecurity.summaryRuleUserField",
                                                                                        {
                                                                                            recordField:
                                                                                                rule
                                                                                                    .config
                                                                                                    .entityField,
                                                                                            userField:
                                                                                                rule
                                                                                                    .config
                                                                                                    .userField,
                                                                                        }
                                                                                    ) ||
                                                                                        `Users whose ${rule.config.userField} matches the record's ${rule.config.entityField}`)}
                                                                                {rule.type ===
                                                                                    "entityField" &&
                                                                                    (t(
                                                                                        "dataSecurity.summaryRuleEntity",
                                                                                        {
                                                                                            field: rule
                                                                                                .config
                                                                                                .fieldName,
                                                                                            value: rule
                                                                                                .config
                                                                                                .fieldValue,
                                                                                        }
                                                                                    ) ||
                                                                                        `Only records where ${rule.config.fieldName} = "${rule.config.fieldValue}"`)}
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                )}
                                                            {(!rulesByOperation.read ||
                                                                rulesByOperation
                                                                    .read
                                                                    .length ===
                                                                    0) && (
                                                                <div className="text-gray-500 italic">
                                                                    {requiresLogin
                                                                        ? t(
                                                                              "dataSecurity.summaryNoRulesLoggedIn"
                                                                          ) ||
                                                                          "Any logged-in user can access"
                                                                        : t(
                                                                              "dataSecurity.summaryNoRulesPublic"
                                                                          ) ||
                                                                          "Anyone can access (public)"}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Technical Query - For Debugging */}
                                            {isEditing &&
                                                rulesByOperation[currentTab] &&
                                                rulesByOperation[currentTab]
                                                    .length > 0 && (
                                                    <details className="border rounded-lg bg-gray-50/50">
                                                        <summary className="cursor-pointer p-2 hover:bg-gray-100 rounded-lg text-[11px] text-gray-400">
                                                            {t(
                                                                "dataSecurity.technicalQuery"
                                                            ) ||
                                                                "For debugging: view technical query"}
                                                        </summary>
                                                        <div className="p-3 pt-0">
                                                            <pre className="text-[10px] bg-white p-2 rounded border overflow-x-auto text-gray-500">
                                                                {JSON.stringify(
                                                                    convertRulesToMongoQuery(
                                                                        rulesByOperation,
                                                                        ruleOperators[
                                                                            table
                                                                                .id
                                                                        ] || {}
                                                                    )[
                                                                        currentTab
                                                                    ],
                                                                    null,
                                                                    2
                                                                )}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}

                                            {isEditing && (
                                                <div className="flex gap-2 pt-2 border-t">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            cancelEditing(
                                                                table.id
                                                            )
                                                        }
                                                        className="flex-1"
                                                    >
                                                        {t("common.cancel") ||
                                                            "Cancel"}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() =>
                                                            saveRules(table)
                                                        }
                                                        disabled={
                                                            isSavingAccess
                                                        }
                                                        className="flex-1"
                                                    >
                                                        {isSavingAccess
                                                            ? t(
                                                                  "common.saving"
                                                              ) || "Saving..."
                                                            : t(
                                                                  "common.save"
                                                              ) || "Save"}
                                                    </Button>
                                                </div>
                                            )}
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}

                        {/* Common example - clearly discoverable */}
                        <details className="mt-8 group">
                            <summary className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                                <span className="uppercase tracking-wide">
                                    {t("dataSecurity.example.label") ||
                                        "Common example"}
                                </span>
                                <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                            </summary>
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                                <p className="mb-2">
                                    {t("dataSecurity.example.description") ||
                                        "A task table where:"}
                                </p>
                                <ul className="space-y-1 ml-3">
                                    <li>
                                        •{" "}
                                        {t("dataSecurity.example.point1") ||
                                            "Anyone can create tasks"}
                                    </li>
                                    <li>
                                        •{" "}
                                        {t("dataSecurity.example.point2") ||
                                            "Only the creator can edit them"}
                                    </li>
                                    <li>
                                        •{" "}
                                        {t("dataSecurity.example.point3") ||
                                            "Admins can delete any task"}
                                    </li>
                                </ul>
                            </div>
                        </details>
                    </div>
                )}
            </div>
        </div>
    );
}

export default DataSecurityPanel;
